import SearchedMemberListItem from "./SearchedMemberListItem";
import { usePaginatedMembersWithPrefixSearch } from "@/middleware/hooks/usePaginatedMembersWithPrefixSearch";
import { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useToast } from "@/hooks/useToast";

type Props = {
  searchTerm: string;
};

export default function SearchedMembersList({ searchTerm }: Props) {
  const { toast } = useToast();

  const {
    status,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    data,
    fetchNextPage,
    error,
  } = usePaginatedMembersWithPrefixSearch({
    searchTerm,
  });

  const results = data ? data.pages.flat() : [];

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  useEffect(() => {
    if (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Cannot load members",
        variant: "destructive",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= results.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNextPage,
    fetchNextPage,
    results.length,
    isFetchingNextPage,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <>
      {isFetching && status === "pending" ? (
        <>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
            <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
          </div>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
            <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
          </div>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
            <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
          </div>
        </>
      ) : (
        <div
          ref={parentRef}
          className="custom-scroll"
          style={{
            height: `100%`,
            width: `100%`,
            overflow: "auto",
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > results.length - 1;
              const member = results[virtualRow.index];

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {isLoaderRow ? (
                    hasNextPage && (
                      <>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                          <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                          <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                          <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                        </div>
                      </>
                    )
                  ) : (
                    <SearchedMemberListItem
                      key={member.username}
                      username={member.username}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
